import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import { useNavigate } from "react-router-dom";

import Navbar from "./talent/NavigationTalent";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import MediaSelectionDialog from "./MediaSelectionDialog";
import GlobalNotification from "./components/GlobalNotification";
import Footer from "./Footer";

const SubmissionHistory = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);

  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();
  
  const userRole = localStorage.getItem("userRole");

  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchSubmissions = async () => {
    setLoading(true);

    try {
      const res = await api.get(ENDPOINTS.getUserJobHistory({ userId }));
      setSubmissions(res.data || []);
    } catch (error) {
      console.error("Error fetching submission history", error);
      showToast("Failed to load submission history.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Submission History";
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [userId]);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );

  return (
    <div>
      <Navbar />

      <div className="background-prop">
        <div
          className="shadow"
          style={{ maxWidth: "1200px", margin: "auto", padding: "20px" }}
        >
          <GlobalNotification
  userId={userId}
  userRole={userRole}
/>
          <Box sx={{ maxWidth: "1000px", mx: "auto", py: 5, px: 2 }}>
            <Typography variant="h5" fontWeight="bold" mb={3}>
              Submission History
            </Typography>

            {submissions.map((submission, index) => (
              <React.Fragment key={index}>
                <Grid container alignItems="center" spacing={2} py={2}>
                  {/* Role Title */}
                  <Grid item xs={12} md={4}>
                    <Typography fontWeight="500">
                      {submission.title} -{" "}
                      {submission.roleName || "Untitled Role"}
                    </Typography>
                  </Grid>

                  {/* Date */}
                  <Grid item xs={12} md={4}>
                    <Typography color="text.secondary">
                      <strong>Date Applied:</strong>{" "}
                      {new Date(submission.dateRecordSubmitted).toLocaleString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                          hour: "numeric",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        },
                      )}
                    </Typography>
                  </Grid>

                  {/* Notification Chip */}
                  <Grid item xs={6} md={2}>
                    {submission.protectFlag && (
                      <Chip
                        icon={<NotificationsActiveIcon />}
                        label="Notification"
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Grid>

                  {/* Actions */}
                  <Grid
                    item
                    xs={12}
                    md={2}
                    display="flex"
                    justifyContent="flex-end"
                    gap={1}
                  >
                    {/* Edit Submission */}
                    <IconButton
                      size="small"
                      aria-label="Edit Submission"
                      onClick={() => {
                        setEditingSubmission(submission);
                        setEditDialogOpen(true);
                      }}
                    >
                      <NoteAltOutlinedIcon
                        sx={{
                          color: submission.coverMessage?.trim()
                            ? "success.main"
                            : "error.main",
                        }}
                      />
                    </IconButton>

                    {/* View Notice */}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const url = `${window.location.origin}/notice/${submission.noticeId}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                      }}
                    >
                      View Notice
                    </Button>
                  </Grid>
                </Grid>

                <Divider />
              </React.Fragment>
            ))}
          </Box>
        </div>
      </div>

      {/* EDIT SUBMISSION DIALOG */}
      {editingSubmission && (
        <MediaSelectionDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingSubmission(null);
          }}
          userId={userId}
          notice={{ noticeId: editingSubmission.noticeId }}
          selectedRoleId={editingSubmission.roleId}
          selectedRoleName={editingSubmission.roleName}
          existingSubmission={editingSubmission}
          isEdit={true}
          onSubmitSuccess={(msg) => {
            showToast(msg || "Submission updated", "success");
            fetchSubmissions();
            setEditDialogOpen(false);
            setEditingSubmission(null);
          }}
          onSubmitError={(msg) =>
            showToast(msg || "Failed to update submission", "error")
          }
        />
      )}

      {/* Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          variant="filled"
        >
          {toastMessage}
        </Alert>
      </Snackbar>
      
      <Footer />
    </div>
  );
};

export default SubmissionHistory;
